import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Phone, Hash, MapPin, Briefcase, Clock, PhoneCall, FileText, CalendarCheck, MapPinned, Clock3, Loader2 } from 'lucide-react';
import tenant from '../../config/tenant';

import { getEmployeeProfilePicUrl } from '../../utils/employeeUtils';
import { getLocationName } from '../../utils/employeeUtils';
import { fetchAuthedBlobUrl } from '../../utils/authedFile';
import { PENDING_SOLID, PENDING_CHIP } from '../../constants/pendingTheme';
import { isPendingApproval } from '../../utils/employeeApproval';
import { formatExperienceLong } from '../../utils/employeeValidation';
import { hexToRgba } from '../../utils/color';

const IMAGE_BASE = tenant.api.employeeImageBaseUrl;

const fmt = (v) => (!v || v === 'null' || v === 'undefined') ? null : String(v).trim();

function getInitials(emp) {
  const full = fmt(emp.HED_REPORT_NAME) ||
    [fmt(emp.HED_FIRST_NAME), fmt(emp.HED_LAST_NAME)].filter(Boolean).join(' ') ||
    fmt(emp.p_report_name) ||
    `${fmt(emp.p_first_name) || ''} ${fmt(emp.p_last_name) || ''}`.trim();
  if (!full) return '?';
  return full.split(/[\s.]+/).filter(Boolean).slice(0, 2).map((p) => p[0].toUpperCase()).join('');
}

function calcYearsWorked(recruitmentDate) {
  if (!recruitmentDate) return null;
  const start = new Date(recruitmentDate);
  if (isNaN(start.getTime())) return null;
  const now = new Date();
  let years = now.getFullYear() - start.getFullYear();
  const m = now.getMonth() - start.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < start.getDate())) years--;
  return years >= 0 ? years : null;
}

// const ACCENT_COLORS = [
//   { bar: 'bg-[#d6264b]',      badge: 'bg-[#fdf2f5] text-[#a81a39]',    ring: 'ring-[#f7c3ce]'   },
// ];

// function getAccent(dept) {
//   const code = (dept || 'X').toUpperCase();
//   return ACCENT_COLORS[code.charCodeAt(0) % ACCENT_COLORS.length];
// }

const accent = tenant.theme.accent;

function EmployeeImage({ directUrl, serviceNo, initials, accentClassName, accentStyle, uploadedImage }) {
  const [apiImgSrc, setApiImgSrc] = useState(null);
  const [apiLoading, setApiLoading] = useState(false);
  const [apiFailed, setApiFailed] = useState(false);

  /* `new Image()` and `<img src>` cannot carry the auth-key header, so the
     picture endpoint answered 401 and every avatar fell back to initials. The
     file is fetched with the header instead and shown from a blob URL. */
  useEffect(() => {
    let cancelled = false;
    if (uploadedImage) { setApiFailed(false); setApiLoading(false); return undefined; }

    const target = directUrl || (serviceNo ? `${IMAGE_BASE}?serviceNo=${encodeURIComponent(serviceNo)}` : null);
    if (!target) { setApiFailed(true); setApiLoading(false); return undefined; }

    setApiLoading(true);
    setApiFailed(false);

    fetchAuthedBlobUrl(target)
      .then((src) => { if (!cancelled) { setApiImgSrc(src); setApiLoading(false); } })
      .catch(() => { if (!cancelled) { setApiFailed(true); setApiLoading(false); } });

    return () => { cancelled = true; };
  }, [directUrl, serviceNo, uploadedImage]);

  const avatarBase = `w-14 h-14 rounded-xl object-cover shrink-0 ring-2 ring-white shadow-md`;

  if (uploadedImage) return <img src={uploadedImage} alt={initials} className={avatarBase} />;

  if (apiLoading) {
    return (
      <div 
        className={`w-14 h-14 rounded-xl shrink-0 ${accentClassName} bg-opacity-20 flex items-center justify-center`}
        style={accentStyle ? { backgroundColor: hexToRgba(accent.solid, 0.2) } : undefined }
      >
        <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  if (!apiFailed && apiImgSrc) return <img src={apiImgSrc} alt={initials} className={avatarBase} onError={() => setApiFailed(true)} />;

  return (
    <div 
      className={`w-14 h-14 rounded-xl ${accentClassName} flex items-center justify-center shrink-0 shadow-md ring-2 ring-white`}
      style={accentStyle}
    >
      <span className="text-white font-bold text-base">{initials}</span>
    </div>
  );
}

function InfoRow({ icon: Icon, value, iconColor = 'text-gray-400' }) {
  if (!value) return null;
  return (
    <div className="flex items-center gap-2 min-w-0">
      <Icon size={11} className={`shrink-0 ${iconColor}`} />
      <span className="text-xs text-gray-600 truncate">{value}</span>
    </div>
  );
}


export default function EmployeeCard({ employee: emp, onClick, onAttendanceClick, onPhysicalLocationClick, busy }) {
  const pendingApproval = isPendingApproval(emp);
  const images = useSelector((s) => s.images?.images || {});
  const empId         = emp.HED_EMPLOYEE_ID;
  const rawServiceNo  = fmt(emp.HED_SERVICE_NO) || fmt(emp.p_ced_service_no);
  const clickId       = empId || rawServiceNo;
  const serviceNo     = rawServiceNo || '—';
  const name          = fmt(emp.HED_REPORT_NAME) ||
                        [fmt(emp.HED_FIRST_NAME), fmt(emp.HED_LAST_NAME)].filter(Boolean).join(' ') ||
                        fmt(emp.p_report_name) || 'Unknown';
  const designation   = fmt(emp.HED_DESIGNATION_NAME) || fmt(emp.HEC_CATEGORY_NAME) || fmt(emp.p_designation) || fmt(emp.p_initial_category) || null;
  const mobile        = fmt(emp.HED_MOBILE_NO) || fmt(emp.HED_HOME_NO) || fmt(emp.p_mobile) || fmt(emp.p_home_tel) || null;
  const extension     = fmt(emp.HED_OFFICE_NO) || fmt(emp.p_telephone_extension) || null;
  // The location, not the cost centre — see getLocationName.
  const location      = getLocationName(emp) || fmt(emp.HDD_DEPARTMENT_NAME) || null;
  const recruitment   = fmt(emp.HED_DATE_OF_JOINING) || fmt(emp.p_recruitment_date) || null;
  /* Stored compact ("7Y5M"), read out in full ("7 Years 5 Months"). */
  const totalExp      = formatExperienceLong(fmt(emp.HED_TOTAL_EXPERIANCE));
  const yearsWorked   = calcYearsWorked(recruitment);
  const jobDesc       = fmt(emp.HSD_SHIFT_NAME) || fmt(emp.HED_REMARKS) || fmt(emp.p_info) || fmt(emp.p_jcat) || null;
  const isActive      = emp.HED_IS_ACTIVE !== undefined
                        ? Boolean(emp.HED_IS_ACTIVE)
                        : (!emp.p_inactive_type || emp.p_inactive_type === '' || emp.p_inactive_type === '0');
  const uploadedImg   = (rawServiceNo && images[rawServiceNo]) || (empId && images[String(empId)]) || null;
  const directUrl     = getEmployeeProfilePicUrl(emp);
  const deptCode      = fmt(emp.HDD_DEPARTMENT_CODE) || fmt(emp.p_department_code) || 'X';
  // const accent        = getAccent(deptCode);
  const initials      = getInitials(emp);

  // Pending employees carry the muted amber accent throughout the card
  // (avatar, accent bar, service-no chip); approved ones keep the green accent.
  // const avatarBar     = pendingApproval ? PENDING_SOLID : accent.bar;
  // const badgeClass    = pendingApproval ? 'bg-amber-50 text-amber-700' : accent.badge;

  const avatarBarClassName = pendingApproval ? PENDING_SOLID : '';
  const avatarBarStyle = pendingApproval ? undefined : { backgroundColor: accent.solid };
  const badgeClassName = pendingApproval ? 'bg-amber-50 text-amber-700' : '';
  const badgeStyle = pendingApproval ? undefined : { backgroundColor: accent.tint, color: accent.dark };
  
  return (
    <div
      onClick={() => clickId && onClick(clickId)}
      className="group relative bg-white rounded-2xl shadow-sm hover:shadow-xl border border-gray-100 hover:border-gray-200 cursor-pointer transition-all duration-300 hover:-translate-y-1.5 overflow-hidden flex flex-col"
    >
      {/* Top color bar */}
      <div className={`h-1 w-full ${avatarBarClassName}`} style={avatarBarStyle} />

      {/* Physical Location — right side */}
      {/* {onPhysicalLocationClick && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); rawServiceNo && onPhysicalLocationClick(rawServiceNo); }}
          title="Physical Location"
          className="absolute top-3 right-3 z-10 flex items-center justify-center w-7 h-7 rounded-lg bg-white/90 shadow border border-gray-100 text-gray-400 hover:bg-slate-800 hover:text-white hover:border-slate-800 transition-colors"
        >
          <MapPinned size={13} />
        </button>
      )} */}

      {/* Card body */}
      <div className="p-4 flex flex-col gap-3 flex-1"> 
        <div className="flex items-start gap-3">
          <EmployeeImage directUrl={directUrl} serviceNo={rawServiceNo} initials={initials} accentClassName={avatarBarClassName} accentStyle={avatarBarStyle} uploadedImage={uploadedImg} />
          <div className="min-w-0 flex-1">
            <p className="font-bold text-gray-900 text-sm leading-snug line-clamp-2">{name}</p>
            <div className="flex items-center gap-1.5 mt-1 flex-wrap">
              <span className={`text-[10px] font-mono font-semibold px-1.5 py-0.5 rounded ${badgeClassName}`} style={badgeStyle}>
                #{serviceNo}
              </span>
              {/* Pending replaces Active rather than sitting beside it. These
                  cards only appear in the Approvals tab, where the record is
                  not a live employee yet — two status chips there read as
                  contradictory and make an unapproved record look approved. */}
              {pendingApproval ? (
                <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${PENDING_CHIP}`}>
                  Pending
                </span>
              ) : (
                <span className={`flex items-center gap-1 text-[10px] font-medium ${isActive ? 'text-emerald-600' : 'text-gray-400'}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-emerald-500' : 'bg-gray-300'}`} />
                  {isActive ? 'Active' : 'Inactive'}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Details rows */}
        <div className="space-y-1.5 border-t border-gray-50 pt-3">
          {designation && (
            <div className="flex items-center gap-2 min-w-0">
              <Briefcase size={11} className="shrink-0 text-amber-500" />
              <span className="text-xs text-gray-800 font-medium truncate">{designation}</span>
            </div>
          )}
          <InfoRow icon={Phone}    value={mobile}    iconColor="text-blue-400" />
          {extension && (
            <div className="flex items-center gap-2 min-w-0">
              <PhoneCall size={11} className="shrink-0 text-purple-400" />
              <span className="text-xs text-gray-600">Ext: <span className="font-semibold text-gray-700">{extension}</span></span>
            </div>
          )}

          <InfoRow icon={MapPin}   value={location}  iconColor="text-rose-400" />
          {(totalExp || yearsWorked !== null) && (
            <div className="flex items-center gap-2 min-w-0">
              <Clock size={11} className="shrink-0 text-primary-400" />
              <span className="text-xs text-gray-600">
                <span className="font-semibold text-gray-800">{totalExp || `${yearsWorked} yr${yearsWorked !== 1 ? 's' : ''}`}</span> {totalExp ? 'exp' : 'worked'}
              </span>
            </div>
          )}
          {jobDesc && (
            <div className="flex items-start gap-2 min-w-0">
              <FileText size={11} className="shrink-0 text-gray-400 mt-0.5" />
              <span className="text-xs text-gray-500 line-clamp-2">{jobDesc}</span>
            </div>
          )}
        </div>
      </div>

      {/* Approve / Reject now live inside the employee detail modal — the card
          only signals that a decision is waiting and opens the record. */}
      {pendingApproval && (
        <div className="px-4 pb-2">
          <div className={`flex items-center justify-center gap-1.5 text-[11px] font-semibold py-1.5 rounded-lg ${PENDING_CHIP}`}>
            {busy
              ? <Loader2 size={12} className="animate-spin" />
              : <Clock3 size={12} />}
            {busy ? 'Saving…' : 'Open to approve'}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="px-4 pb-3 flex gap-1.5">
        <div className={`flex-1 text-[11px] font-semibold text-center py-1.5 rounded-lg transition-colors ${badgeClassName} group-hover:opacity-90`} style={badgeStyle}>
          View Profile →
        </div>
        {onAttendanceClick && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              const targetSvcNo = rawServiceNo || (empId ? String(empId) : null);
              if (targetSvcNo) onAttendanceClick(targetSvcNo);
            }}
            title="Attendance"
            className="flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1.5 rounded-lg bg-gray-50 text-gray-500 hover:bg-slate-800 hover:text-white transition-colors shrink-0"
          >
            <CalendarCheck size={12} /> Attendance
          </button>
        )}
      </div>
    </div>
  );
}